"use client";

import { useState, type ReactNode } from "react";
import type { LjArticle } from "@/fixtures/lj/fixtures";
import { SummaryGrid } from "./summary-grid";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";

/**
 * `SummaryGrid` with a "View More" that reveals the next batch IN PLACE.
 *
 * The home page's LATEST section had a CTA pointing at `/lj/blog` — a different
 * section of the magazine, so "view more Latest" took the reader away from
 * Latest. QA: "LATEST section: click on the view more button, will load 12 more
 * articles into the current section."
 *
 * The button disappears once everything is shown, rather than staying live and
 * doing nothing — the defect this codebase has filed repeatedly.
 */
export function LoadMoreGrid({
  title,
  articles,
  loadMoreStep,
  viewMoreLabel = "View More",
  ...rest
}: {
  title: string;
  articles: LjArticle[];
  loadMoreStep: number;
  viewMoreLabel?: string;
  adImage?: string;
  adImage2?: string;
  adHref?: string;
  bannerAdImage?: string;
  bannerAdHref?: string;
  bannerAdSplitAt?: number;
  firstSidebar?: "lrec" | "half";
  sidebarTop1?: ReactNode;
  sidebarTop2?: ReactNode;
}) {
  const initial = rest.bannerAdSplitAt ?? 6;
  // Two blocks on the first paint, so the 6th card of each is the native.
  const [visible, setVisible] = useState(Math.min(initial * 2, articles.length));
  const shown = articles.slice(0, visible);
  const hasMore = visible < articles.length;

  return (
    <div className="flex flex-col">
      <SummaryGrid title={title} articles={shown} {...rest} />
      {hasMore && (
        <div className="mt-14 flex justify-center">
          <button
            type="button"
            onClick={() =>
              setVisible((v) => Math.min(v + loadMoreStep, articles.length))
            }
            className="inline-flex items-center px-6 py-3 transition-opacity hover:opacity-80 motion-reduce:transition-none lg:px-10 lg:py-5"
            style={{
              ...LJ_TYPE.navLink,
              fontSize: 12,
              backgroundColor: LJ_COLORS.textPrimary,
              color: LJ_COLORS.textOnDark,
            }}
          >
            {viewMoreLabel}
          </button>
        </div>
      )}
    </div>
  );
}
